import { EditorState, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { EditorView } from '@atlaskit/editor-prosemirror/view';

type Direction = 'up' | 'down';

const findExtensionNodePos = (
  state: EditorState,
  localId: string
): number | null => {
  let foundPos: number | null = null;

  // Find the position of the extension node that matches the given localId.
  // There may be multiple suggestions, but each has a unique localId.
  state.doc.descendants((node, pos) => {
    if (node.type.name === 'extension' && node.attrs.localId === localId) {
      foundPos = pos;
    }
  });

  return foundPos;
};

const moveSelectionToAdjacentNode = (
  outerEditorView: EditorView,
  pos: number,
  direction: Direction
) => {
  const { state, dispatch } = outerEditorView;
  const { tr } = state;

  const createParagraphNodeAtPos = (position: number) => {
    const paragraph = state.schema.nodes.paragraph.create();
    return tr.insert(position, paragraph);
  };

  const updateSelection = (position: number) => {
    const resolvedPos = tr.doc.resolve(position);
    const selection = TextSelection.create(tr.doc, resolvedPos.pos);
    dispatch(tr.setSelection(selection).scrollIntoView());
  };

  // Handle moving the selection
  if (direction === 'up') {
    if (pos === 0) {
      createParagraphNodeAtPos(0); // Create a paragraph at the start
      updateSelection(0);
    } else {
      updateSelection(pos - 1); // Move selection upwards
    }
  } else {
    // We move 2 positions instead of 1 because the extension node itself takes up a full position.
    // Moving only 1 would keep the selection on the extension. This ensures we land outside of it.
    updateSelection(pos + 2); // Move selection downwards
  }

  outerEditorView.focus();
};

const selectNodeAtPos = (outerEditorView: EditorView, pos: number) => {
  const { state, dispatch } = outerEditorView;
  const resolvedPos = state.doc.resolve(pos);
  const selection = TextSelection.near(resolvedPos);

  dispatch(state.tr.setSelection(selection).scrollIntoView());
  outerEditorView.focus();
};

const replaceExtensionWithParagraph = (
  outerEditorView: EditorView,
  pos: number,
  content: string
) => {
  const { state, dispatch } = outerEditorView;
  const node = state.doc.nodeAt(pos);
  if (!node) return;

  // NOTE: Currently, this doesn't correctly insert a paragraph node containing the passed-in text.
  // The paragraph is created with content, but it's somehow not rendering as expected.
  // Will revisit this to ensure proper insertion with content preserved.
  const paragraphContent =
    content.length > 0 ? state.schema.text(content) : null;
  const paragraph = state.schema.nodes.paragraph.create(null, paragraphContent);

  const tr = state.tr
    .replaceWith(pos, pos + node.nodeSize, paragraph)
    .setSelection(TextSelection.near(state.tr.doc.resolve(pos)));

  dispatch(tr.scrollIntoView());
  outerEditorView.focus();
};

export interface HandleKeyDownInput {
  command:
    | 'move-up'
    | 'move-down'
    | 'select-left'
    | 'select-right'
    | 'replace-with-paragraph';
  outerEditorView: EditorView | null;
  extensionNodeId: string;
  content: string;
}

export const handleCodeMirrorKeyDown = ({
  command,
  outerEditorView,
  extensionNodeId,
  content,
}: HandleKeyDownInput): void => {
  if (!outerEditorView) return;
  const { state } = outerEditorView;
  const extensionPos = findExtensionNodePos(state, extensionNodeId);
  if (extensionPos === null) return;

  switch (command) {
    case 'move-down':
      moveSelectionToAdjacentNode(outerEditorView, extensionPos, 'down');
      break;
    case 'move-up':
      moveSelectionToAdjacentNode(outerEditorView, extensionPos, 'up');
      break;
    case 'select-right':
    case 'select-left':
      selectNodeAtPos(outerEditorView, extensionPos);
      break;
    case 'replace-with-paragraph':
      replaceExtensionWithParagraph(outerEditorView, extensionPos, content);
      break;
  }
};
